import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { useAuth } from './AuthContext';
import templatesApiService from '../services/templatesApi';
import { getCurrentDateLocal } from '../utils/dataHelpers';
import { clearGuestRentDraft } from '../utils/guestRentDraft';

// =============================================================================
// НАЧАЛЬНОЕ СОСТОЯНИЕ
// =============================================================================
const initialState = {
    currentContract: null,
    contractId: null,
    isLoading: false,
    isSaving: false,
    error: null,
    mode: 'create' // 'create' | 'edit'
};

// =============================================================================
// REDUCER
// =============================================================================
const contractReducer = (state, action) => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload, error: action.payload ? null : state.error };
        case 'SET_SAVING':
            return { ...state, isSaving: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload, isLoading: false, isSaving: false };
        case 'CLEAR_ERROR':
            return { ...state, error: null };
        case 'LOAD_CONTRACT_SUCCESS':
            return { ...state, currentContract: action.payload.contract, contractId: action.payload.contractId, mode: 'edit', isLoading: false, error: null };
        case 'CREATE_CONTRACT_SUCCESS':
            return { ...state, currentContract: { ...action.payload.contract, data: action.payload.contract.data || state.currentContract.data }, contractId: action.payload.contractId, mode: 'edit', isSaving: false, error: null };
        case 'UPDATE_CONTRACT_SUCCESS':
            return { ...state, currentContract: { ...state.currentContract, id: action.payload.contract.id, lastModified: action.payload.contract.lastModified, }, isSaving: false, error: null };
        case 'UPDATE_CONTRACT_DATA':
            return { ...state, currentContract: { ...state.currentContract, data: action.payload } };
        case 'INITIALIZE_NEW_CONTRACT':
            return { ...state, currentContract: { templateId: action.payload.templateId, data: action.payload.data }, contractId: null, mode: 'create', error: null };
        default:
            return state;
    }
};

// =============================================================================
// КОНТЕКСТ
// =============================================================================
const ContractContext = createContext(null);

// =============================================================================
// ПРОВАЙДЕР
// =============================================================================
export const ContractProvider = ({ children }) => {
    const [state, dispatch] = useReducer(contractReducer, initialState);
    const { isAuthenticated } = useAuth();

    const clearLocalStorageBackups = () => {
        try {
            localStorage.removeItem('contract_backup');
            localStorage.removeItem('pending_contract_data');
            clearGuestRentDraft();
        } catch (error) {
            console.warn('ContractContext: Error clearing localStorage:', error);
        }
    };

    const saveToLocalStorageBackup = (data) => {
        try {
            localStorage.setItem('contract_backup', JSON.stringify(data));
        } catch (error) {
            console.warn('ContractContext: Error saving to localStorage:', error);
        }
    };

    // ✨ ИСПРАВЛЕНИЕ: Все функции, передаваемые в контекст, обернуты в useCallback
    // для предотвращения бесконечного цикла рендеринга.

    const initializeNewContract = useCallback((templateId = 'rent_apartment') => {
    const randomNumber = 1;
    // Получаем текущую дату в формате дд.мм.гггг (локальное время)
    // Используем утилиту для избежания проблем с часовыми поясами
    const currentDate = getCurrentDateLocal();
    
    const defaultData = {
      header: {
        number: randomNumber.toString(),
        city: '',
        date: currentDate
      },
      parties: {
        landlordName: '',
        landlordPassportSerial: '',
        landlordPassportNumber: '',
        tenantName: '',
        tenantPassportSerial: '',
        tenantPassportNumber: ''
      },
      property: {
        roomCount: '',
        totalArea: '',
        includeLivingArea: false,
        livingArea: '',
        address: '',
        buildingFloors: '',
        apartmentFloor: '',
        ownershipConfirmation: {
          type: 'egrn',
          details: ''
        },
        occupancy: {
          type: 'alone',
          members: [{ id: 1, nameAndDob: '' }]
        },
        includeCadastralNumber: false,
        cadastralNumber: '',
        includeOtherParameters: false,
        otherParameters: '',
        // Пункт 3 Акта приема-передачи
        handoverItems: {
          generalDescription: { enabled: false, text: '' },
          rooms: [],
          equipment: [],
          furniture: [],
          customItems: []
        }
      },
      term: {
        termSelection: 'less_than_year', // 'less_than_year' | 'more_than_year'
        durationMonths: 11,
        registrationResponsible: 'landlord',
        registrationCostBearer: 'landlord'
      },
      rightsAndObligations: {
        // 3.1. Обязанности Нанимателя
        returnPeriodDays: 3,
        includeOtherTenantObligations: false,
        otherTenantObligations: '',
        routineRepairs: {
          includeWorkList: false,
          workList: '',
          includeRedevelopmentBan: true,
        },

        tenantRights: {
          includeOtherRights: false,
          otherRights: '',
        },
        // 3.3. Обязанности Наймодателя
        handoverOption: 'on_signing_day',
        handoverPeriodDays: 5,
        capitalRepairs: {
          enabled: true,
          includePeriod: false,
          period: '5 лет',
          includeWorkList: false,
          workList: '',
        },
        // 3.4. Права Наймодателя
        includeInspectionClause: true,
        inspectionPeriod: '1 раз в месяц',
      },
      usageConditions: {
        smoking: 'forbidden',
        openFlames: 'forbidden',
        pets: 'allowed',
        meterReadings: {
          responsible: 'tenant_to_landlord',
          dayOfMonth: 25,
        },
        includeOtherConditions: false,
        otherConditions: '',
      },
      payment: {
        monthlyAmount: { amount: '', amountWords: '' },
        revisionConditions: { type: 'fixed' }, // 'none', 'annual', 'fixed'
        paymentMethod: { type: 'bank', conditions: 'current' }, // 'bank', 'cash'
        utilityPayment: { type: 'tenant' }, // 'tenant', 'tenantMeters', 'landlord'
        internetPayment: { enabled: false, payer: 'landlord' }, // 'landlord', 'tenant'
        securityDeposit: { enabled: false, amount: '', amountWords: '', returnType: 'credit' }, // 'return', 'credit'
        agentCommission: { enabled: false, agentName: '' }
      },
      financialLiability: {
        latePayment: { enabled: false, gracePeriodDays: 5, penaltyPercent: 1 },
        lateHandover: { enabled: false, gracePeriodDays: 5, penaltyPercent: 1 },
        propertyDamage: {
          enabled: false,
          options: {
          fine: { enabled: false, amount: 10000, days: 10 }, 
          replacement: { enabled: false, days: 10 },
          compensation: { enabled: false, days: 10 },
          },
        },
        otherConditions: { enabled: false, text: '' },
      },
      disputeResolution: {
        jurisdiction: {
          enabled: false,
          courtName: '',
          courtAddress: ''
        },
        claimProcedure: {
          enabled: true,
          claimPeriodDays: 10
        },
        claimSendingMethods: {
          byPost: true,
          byEmail: false,
          byMessenger: false
        }
      },
      attachments: {
        handoverAct: { enabled: true, title: 'Акт приема-передачи жилого помещения' },
        returnAct: { enabled: true, title: 'Акт возврата жилого помещения', date: '', city: '' },
        paymentAct: { enabled: false, title: 'Акт приема-передачи денежных средств' },
        egrnExtract: { enabled: false, number: '', date: '' },
        otherDocument: { enabled: false, name: '', number: '', date: '' }
      },
      finalProvisions: {}
      ,
      signatures: {
        landlord: {
          fullName: '',
          address: '',
          passportSerial: '',
          passportNumber: '',
          passportIssuedBy: '',
          passportIssueDate: '',
          passportDeptCode: '',
          phone: '',
          email: '',
          paymentDetails: ''
        },
        tenant: {
          fullName: '',
          address: '',
          passportSerial: '',
          passportNumber: '',
          passportIssuedBy: '',
          passportIssueDate: '',
          passportDeptCode: '',
          phone: '',
          email: '',
          paymentDetails: ''
        },
        // Акт приема-передачи денежных средств (динамический номер: 2, если включен)
        moneyTransferAct: {
          paymentType: 'current', // 'current', 'previous', 'advance'
          paymentDetails: {
            amount: '',
            amountWords: '',
            monthsCount: ''
          }
        }
      }
    };
    dispatch({ type: 'INITIALIZE_NEW_CONTRACT', payload: { templateId, data: defaultData } });
  }, []);

    /** Восстановить данные черновика (гость или после отложенной загрузки) */
    const restoreContractData = useCallback((templateId, data) => {
        if (!data || typeof data !== 'object') return;
        dispatch({ type: 'INITIALIZE_NEW_CONTRACT', payload: { templateId, data } });
    }, []);

    const loadContract = useCallback(async (contractId) => {
        if (!isAuthenticated) {
            dispatch({ type: 'SET_ERROR', payload: 'Необходима авторизация для загрузки договора' });
            return;
        }
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await templatesApiService.getContract(contractId);
            if (response.status === 'success') {
                clearLocalStorageBackups();
                dispatch({ type: 'LOAD_CONTRACT_SUCCESS', payload: { contract: response.data, contractId: response.data.id } });
            } else {
                throw new Error(response.error?.message || 'Ошибка загрузки договора');
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
        }
    }, [isAuthenticated]); // Зависит от isAuthenticated.

    const saveContract = useCallback(async () => {
        if (!state.currentContract) return null;
        if (!isAuthenticated) {
            saveToLocalStorageBackup(state.currentContract.data);
            return null;
        }
        dispatch({ type: 'SET_SAVING', payload: true });
        try {
            let response;
            if (state.mode === 'create' || !state.contractId) {
                response = await templatesApiService.createContract(state.currentContract.templateId, state.currentContract.data);
                if (response.status === 'success') {
                    clearLocalStorageBackups();
                    dispatch({ type: 'CREATE_CONTRACT_SUCCESS', payload: { contract: response.data, contractId: response.data.id } });
                    return response.data;
                }
            } else {
                response = await templatesApiService.updateContract(state.contractId, state.currentContract.data);
                if (response.status === 'success') {
                    clearLocalStorageBackups();
                    dispatch({ type: 'UPDATE_CONTRACT_SUCCESS', payload: { contract: response.data } });
                    return response.data;
                }
            }
            throw new Error(response.error?.message || 'Ошибка сохранения договора');
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
            saveToLocalStorageBackup(state.currentContract.data);
            throw error;
        }
    }, [state, isAuthenticated]); // Зависит от всего state и isAuthenticated.

    const updateContractData = useCallback((newData) => {
        if (!newData || typeof newData !== 'object' || !state.currentContract) return;
        dispatch({ type: 'UPDATE_CONTRACT_DATA', payload: newData });
    }, [state.currentContract]); // Зависит от наличия currentContract.

    const clearError = useCallback(() => {
        dispatch({ type: 'CLEAR_ERROR' });
    }, []); // Не имеет зависимостей.

    const contextValue = {
        ...state,
        initializeNewContract,
        restoreContractData,
        loadContract,
        saveContract,
        updateContractData,
        clearError,
        isNewContract: () => state.mode === 'create' || !state.contractId,
    };

    return (
        <ContractContext.Provider value={contextValue}>
            {children}
        </ContractContext.Provider>
    );
};

// =============================================================================
// ХУК
// =============================================================================
export const useContract = () => {
    const context = useContext(ContractContext);
    if (!context) {
        throw new Error('useContract должен использоваться внутри ContractProvider');
    }
    return context;
};

export default ContractContext;